import React from 'react';
import { Sentry } from '../Utils/sentryConfig';
import { Box, Typography, Button } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

/**
 * Error fallback UI displayed when an error boundary catches an error
 */
const ErrorFallback = ({ error, resetError }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: 4,
      textAlign: 'center',
      backgroundColor: '#f5f5f5',
    }}
  >
    <ErrorOutlineIcon
      sx={{
        fontSize: 80,
        color: 'error.main',
        mb: 2,
      }}
    />
    <Typography variant="h4" gutterBottom sx={{ fontWeight: 500 }}>
      Oops! Something went wrong
    </Typography>
    <Typography
      variant="body1"
      color="text.secondary"
      sx={{ mb: 3, maxWidth: 600 }}
    >
      We've been notified and are working on it. Please try refreshing the page
      or come back later.
    </Typography>
    {error && process.env.NODE_ENV === 'development' && (
      <Box
        sx={{
          mb: 3,
          p: 2,
          backgroundColor: '#fff',
          borderRadius: 1,
          maxWidth: 800,
          textAlign: 'left',
          border: '1px solid #e0e0e0',
        }}
      >
        <Typography
          variant="caption"
          component="pre"
          sx={{
            fontSize: 12,
            fontFamily: 'monospace',
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {error.toString()}
          {error.stack && `\n\n${error.stack}`}
        </Typography>
      </Box>
    )}
    <Button variant="contained" onClick={resetError} sx={{ minWidth: 120 }}>
      Try Again
    </Button>
  </Box>
);

/**
 * Sentry Error Boundary Component
 *
 * Wraps components to catch React errors and report them to Sentry.
 * Displays a user-friendly fallback UI when errors occur.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to wrap
 */
export const SentryErrorBoundary = ({ children }) => {
  return (
    <Sentry.ErrorBoundary
      fallback={ErrorFallback}
      showDialog={false} // Set to true to show Sentry user feedback dialog
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};
